import { useContext, useMemo } from "react";
import { Alert, Col } from "react-bootstrap";
import { AxisOptions, Chart } from "react-charts";
import { securityCategoryAssetClassColumnsMap } from "../firm/lib";
import { ThemeContext } from "../lib/display";
import { formatPercent } from "../lib/numbers";

export type AssetAllocationDataPoint = {
  category: string;
  allocation: number;
  displayOrder?: number;
};
export type AssetAllocationSeries = {
  label: string;
  data: AssetAllocationDataPoint[];
};

const AssetAllocationBarChart = ({
  data,
  excludedSecuritiesPercentage,
}: {
  data: AssetAllocationSeries[];
  excludedSecuritiesPercentage?: number;
}) => {
  // Normalize all series to have the same categories in the same order,
  // filling missing categories with allocation: 0, then filter out
  // categories where all series have 0 allocation.
  const normalizedData = useMemo(() => {
    // Collect unique categories and sort by displayOrder from the API.
    // Falls back to first-appearance order if displayOrder is not present.
    const orderMap = new Map<string, number>();
    for (const series of data) {
      for (const point of series.data) {
        if (!orderMap.has(point.category)) {
          orderMap.set(
            point.category,
            point.displayOrder ?? Number.MAX_SAFE_INTEGER,
          );
        }
      }
    }
    const allCategories = [...orderMap.keys()].sort(
      (a, b) => (orderMap.get(a) ?? 0) - (orderMap.get(b) ?? 0),
    );

    // Find categories where at least one series has a non-zero allocation
    const hasValue = new Set<string>();
    for (const series of data) {
      for (const point of series.data) {
        if (point.allocation !== 0) {
          hasValue.add(point.category);
        }
      }
    }
    const activeCategories = allCategories.filter((c) => hasValue.has(c));

    // Build normalized series with all active categories in consistent order
    return data.map((series) => {
      const allocationMap = new Map(
        series.data.map((p) => [p.category, p.allocation]),
      );
      return {
        ...series,
        data: activeCategories.map((category) => ({
          category,
          allocation: allocationMap.get(category) ?? 0,
        })),
      };
    });
  }, [data]);

  const defaultColors = ["#54636d", "#5D5FEF", "#DDE0E2"];
  const hasValidData = normalizedData.some(
    (series) =>
      Array.isArray(series.data) &&
      series.data.length > 0 &&
      series.data.some(
        (point) =>
          point &&
          typeof point.category === "string" &&
          typeof point.allocation === "number" &&
          !isNaN(point.allocation),
      ),
  );

  const primaryAxis = useMemo(
    (): AxisOptions<AssetAllocationDataPoint> => ({
      getValue: (datum) =>
        securityCategoryAssetClassColumnsMap.get(datum.category)?.label ??
        datum.category,
    }),
    [],
  );

  const secondaryAxes = useMemo(
    (): AxisOptions<AssetAllocationDataPoint>[] => [
      {
        getValue: (datum) => datum.allocation,
        elementType: "bar",
        hardMin: 0,
        hardMax: 1,
        formatters: {
          scale: (value: number | null) =>
            value === null ? "" : formatPercent(value),
        },
      },
    ],
    [],
  );

  const { currentTheme } = useContext(ThemeContext);

  return normalizedData.length <= 0 || !hasValidData ? (
    <Alert>No data available.</Alert>
  ) : (
    <div>
      {data.length > 1 && (
        <div className="text-center">
          <div className="d-inline-flex justify-content-between gap-4 border rounded px-3 py-1">
            {data.map((series, index) => (
              <div key={index} className="d-inline-block">
                <span className="align-middle me-2">{series.label ?? ""}</span>
                <div
                  className="d-inline-block rounded-circle align-middle"
                  style={{
                    backgroundColor: defaultColors[index],
                    width: "1rem",
                    height: "1rem",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <Col>
        <div
          className="text-center"
          style={{
            height: "375px",
          }}
        >
          <Chart
            options={{
              data: normalizedData,
              primaryAxis,
              secondaryAxes,
              defaultColors,
              dark: currentTheme === "dark",
              padding: { left: 0, right: 0, top: 0, bottom: 0 },
            }}
          />
        </div>
      </Col>
      {excludedSecuritiesPercentage != null &&
        excludedSecuritiesPercentage > 0 && (
          <div className="text-muted small fst-italic mt-2">
            *Excluded securities (
            {formatPercent(excludedSecuritiesPercentage / 100, 0)} of holdings)
            are not calculated in the target allocation.
          </div>
        )}
    </div>
  );
};

export default AssetAllocationBarChart;
